/**
 * NotificationRuleWizard.jsx — create/edit ONE custom notification rule.
 *
 * Routes (registered in App.jsx, SuperAdmin-gated exactly like the Admin ▸
 * Notifications tab): /admin/notifications/rules/new and
 * /admin/notifications/rules/:id. Spec: notification_rules_logic.md §8.
 *
 * Five steps, matching the NewCaseWizard idiom (step pills, Back/Next,
 * inline validation on Next):
 *   1 When       — rule kind (event / scheduled); event rules pick 1+ triggers,
 *                  scheduled rules pick a re-fire mode (daily 08:00 UTC scan).
 *   2 Conditions — the shared RuleBuilder, leaf fields filtered per kind +
 *                  triggers via fieldsForRuleKind (the frontend mirror of the
 *                  server's per-kind gate in notificationSchema.js).
 *   3 Recipients — the shared RecipientEditor, incl. the 'group' kind backed
 *                  by the admin-managed recipientGroups config.
 *   4 Message    — optional subject suffix + body note. Data floor applies:
 *                  notifications carry Case ID, clinic and a link only.
 *   5 Review     — one-line summary + live server preview (debounced dry-run
 *                  via POST /api/notifications/rules/preview), then Save.
 *
 * Saving upserts this rule into the governed `notificationRules` config array
 * (publish: true, audit hash-chain) and returns to Admin ▸ Notifications.
 * Legacy v1 rules (single `trigger` string) open transparently — normalizeRule
 * folds the trigger into a 1-element `triggers` array; saving rewrites the
 * rule in canonical v2 shape. The server re-validates on save either way.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppConfig } from '../contexts/AppConfigContext.jsx';
import { getTherapieMapsData } from '../api/blobClient.js';
import { previewNotificationRule } from '../api/client.js';
import RuleBuilder from '../components/RuleBuilder.jsx';
import RecipientEditor, { recipientSummary, KIND_LABELS } from '../components/RecipientEditor.jsx';
import { TRIGGER_LABELS } from '../components/NotificationsAdmin.jsx';
import {
  RULE_FIELDS, fieldsForRuleKind, ruleIsPreviewable, isGroup,
  isOutstandingRequirement, emptyOutstandingRequirement, outstandingRequirementIsComplete,
  OUTSTANDING_REQUIREMENT_OPTIONS, describeNode, emptyRule,
} from '../utils/ruleFields.js';
import { buildRuleFieldValues } from '../utils/ruleFieldValues.js';
import { findDisallowedNode, NESTED_OUTSTANDING_MESSAGE } from '../lib/notificationRuleGuards.js';
import {
  NOTIFICATION_TRIGGERS,
  SUBJECT_SUFFIX_MAX,
  NOTE_MAX,
  COALESCE_WINDOW_MIN,
  COALESCE_WINDOW_MAX,
  COALESCE_MAX_WINDOW_DEFAULT,
  COALESCE_MAX_WINDOW_MAX,
  normalizeRule,
  validateNotificationRule,
  isStaffEmail,
  EXCEPT_CHANNEL,
  MAX_ESCALATION_STEPS,
  findDayThreshold,
} from '@shared/notificationSchema.js';

// Same id convention as the rules the admin list used to mint.
const freshRuleId = () => 'rule-' + Math.random().toString(36).slice(2, 10);

const STEPS = ['When', 'Conditions', 'Recipients', 'Message', 'Review'];

const FIELD_LABELS = Object.fromEntries(RULE_FIELDS.map(f => [f.key, f.label]));

// ── Draft <-> rule mapping ────────────────────────────────────────────────────
// The draft keeps BOTH kinds' inputs (triggers AND refire) so switching kind
// back and forth never loses what was typed; ruleFromDraft() emits only the
// keys valid for the chosen kind (the validator rejects cross-kind keys).

// Default coalesce quiet-period (minutes) shown when the toggle is first on.
const COALESCE_WINDOW_DEFAULT = 5;

function freshDraft() {
  return {
    id: freshRuleId(),
    name: '',
    enabled: true,
    kind: 'event',
    triggers: ['case_submitted'],
    refire: 'once',
    perCase: false,
    coalesceOn: false,
    coalesceWindowMinutes: COALESCE_WINDOW_DEFAULT,
    coalesceMaxWindowMinutes: COALESCE_MAX_WINDOW_DEFAULT,
    conditions: null,
    recipients: [],
    escalation: [],
    subjectSuffix: '',
    note: '',
  };
}

function draftFromRule(rule) {
  const norm = normalizeRule(rule);
  const window = Number.isInteger(norm.coalesce?.windowMinutes) ? norm.coalesce.windowMinutes : null;
  // An existing coalesce rule with no maxWindowMinutes gets the default cap.
  const maxWindow = Number.isInteger(norm.coalesce?.maxWindowMinutes)
    ? norm.coalesce.maxWindowMinutes
    : COALESCE_MAX_WINDOW_DEFAULT;
  return {
    id: norm.id,
    name: norm.name || '',
    enabled: norm.enabled !== false,
    kind: norm.kind === 'scheduled' ? 'scheduled' : 'event',
    triggers: Array.isArray(norm.triggers) ? norm.triggers : [],
    refire: ['rearm_on_activity', 'every_run'].includes(norm.refire) ? norm.refire : 'once',
    perCase: norm.perCase === true,
    coalesceOn: window != null,
    coalesceWindowMinutes: window ?? COALESCE_WINDOW_DEFAULT,
    coalesceMaxWindowMinutes: maxWindow,
    conditions: norm.conditions ?? null,
    recipients: Array.isArray(norm.recipients) ? norm.recipients : [],
    escalation: Array.isArray(rule?.escalation) ? rule.escalation : [],
    subjectSuffix: norm.subjectSuffix || '',
    note: norm.note || '',
  };
}

/** The canonical v2 rule this draft would save (empty condition group → null). */
function ruleFromDraft(draft) {
  const hasConditions = isGroup(draft.conditions) && (draft.conditions.rules || []).length > 0;
  const rule = {
    id: draft.id,
    name: draft.name.trim(),
    enabled: draft.enabled !== false,
    kind: draft.kind,
    conditions: hasConditions ? draft.conditions : null,
    recipients: draft.recipients,
  };
  if (draft.kind === 'event') {
    rule.triggers = draft.triggers;
    // Batch rapid updates → coalesce:{ windowMinutes, maxWindowMinutes } when on
    // (debounce quiet-period + safety cap); key omitted entirely when off.
    if (draft.coalesceOn) {
      rule.coalesce = {
        windowMinutes: draft.coalesceWindowMinutes,
        maxWindowMinutes: draft.coalesceMaxWindowMinutes,
      };
    }
  } else {
    rule.refire = draft.refire;
    // One email per case → perCase:true; the key is omitted when off (the
    // default) so stored rules stay tight and back-compatible.
    if (draft.perCase) rule.perCase = true;
    if (draft.escalation.length) rule.escalation = draft.escalation;
  }
  const suffix = draft.subjectSuffix.trim();
  if (suffix) rule.subjectSuffix = suffix;
  const note = draft.note.trim();
  if (note) rule.note = note;
  return rule;
}

// ── Per-step validation (Next gates; Save re-runs the full shared validator) ──

// findDisallowedNode (disallowed leaf field / banned aggregate / NESTED
// outstanding-requirement node) lives in lib/notificationRuleGuards.js so it
// is unit-tested; see conditionStepError below for the messages.

/** Does the condition tree contain a fileCount node (at any depth)? */
function treeHasFileCount(node) {
  if (!node || typeof node !== 'object') return false;
  if (node.type === 'fileCount') return true;
  return Array.isArray(node.rules) && node.rules.some(treeHasFileCount);
}

// fileCount conditions can never see the wizard's own uploads on submission:
// NewCaseWizard creates the case FIRST (dispatching case_submitted) and uploads
// the slot files AFTER, so every file count is 0 at that dispatch. A rule fired
// ONLY by case_submitted with a fileCount condition is therefore either dead
// ("> 0" never fires) or fires for every submission ("equals 0") — warn, and
// point at attachment_uploaded, which fires per upload with real counts.
function fileCountAtSubmitWarning(draft) {
  return draft.kind === 'event'
    && draft.triggers.length > 0
    && draft.triggers.every(t => t === 'case_submitted')
    && treeHasFileCount(draft.conditions);
}

// ── Outstanding-requirement nodes — edited OUTSIDE RuleBuilder ───────────────
// The shared RuleBuilder has no editor row for `outstandingRequirement` nodes
// yet, so the wizard keeps them as TOP-LEVEL members of the conditions group
// and edits them in its own small editor next to the builder: the builder sees
// the group WITHOUT those nodes (splitOutstanding) and every change from either
// side is re-joined (joinOutstanding). Semantics are unchanged — the nodes are
// ordinary members of the top-level ALL/ANY group. A stored rule that carries
// one INSIDE a sub-group cannot be edited here (RuleBuilder would render it as
// a field condition and rewrite it) — conditionStepError rejects it explicitly.
function splitOutstanding(tree) {
  if (!isGroup(tree)) return { base: tree, outstanding: [] };
  const rules = Array.isArray(tree.rules) ? tree.rules : [];
  return {
    base: { ...tree, rules: rules.filter(n => !isOutstandingRequirement(n)) },
    outstanding: rules.filter(isOutstandingRequirement),
  };
}
function joinOutstanding(base, outstanding) {
  const group = isGroup(base) ? base : emptyRule();
  const rules = (Array.isArray(group.rules) ? group.rules : []).filter(n => !isOutstandingRequirement(n));
  return { ...group, rules: [...rules, ...(Array.isArray(outstanding) ? outstanding : [])] };
}

function OutstandingRequirementEditor({ nodes, onChange }) {
  const list = Array.isArray(nodes) ? nodes : [];
  const setAt = (i, next) => onChange(list.map((n, j) => (j === i ? next : n)));
  const remove = (i) => onChange(list.filter((_, j) => j !== i));
  // A stored key outside the picker vocabulary (a per-case requirement type key)
  // stays selectable so the row shows what is saved.
  const optionsFor = (node) => OUTSTANDING_REQUIREMENT_OPTIONS.some(o => o.key === node.key)
    ? OUTSTANDING_REQUIREMENT_OPTIONS
    : [...OUTSTANDING_REQUIREMENT_OPTIONS, { key: node.key, label: `${node.key} (requirement type)` }];
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>Outstanding requirements</div>
      <div className="field-hint" style={{ marginTop: 0, marginBottom: 8 }}>
        Matches cases that still have a required document, field or sign-off missing (waived items and
        uploaded files never count). Added to the top-level group above and combined with its ALL / ANY mode.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {list.map((node, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap',
                          padding: '4px 6px', border: '1px dashed var(--border-strong)', borderRadius: 8 }}>
              <select className="input sm" value={node.key ?? 'any'}
                      onChange={e => setAt(i, { ...node, key: e.target.value })}
                      style={{ fontSize: 12, padding: '3px 6px' }} aria-label="Outstanding requirement">
                {optionsFor(node).map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
              <span style={{ fontSize: 11, color: 'var(--text-3)' }}>outstanding for at least</span>
              <input className="input sm" type="number" min="0"
                     value={Number.isFinite(node.minDays) ? node.minDays : ''}
                     onChange={e => {
                       const n = Number(e.target.value);
                       setAt(i, { ...node, minDays: e.target.value === '' ? '' : (Number.isInteger(n) && n >= 0 ? n : node.minDays) });
                     }}
                     style={{ fontSize: 12, padding: '3px 6px', width: 64 }}
                     aria-label="Minimum days outstanding" />
              <span style={{ fontSize: 11, color: 'var(--text-3)' }}>day(s) — 0 = missing at all</span>
              <button type="button" className="btn ghost sm" onClick={() => remove(i)}
                      title="Remove outstanding requirement condition" aria-label="Remove outstanding requirement condition"
                      style={{ fontSize: 13, color: 'var(--danger)', lineHeight: 1, padding: '2px 8px' }}>×</button>
            </div>
            {outstandingRequirementIsComplete(node) && (
              <div style={{ fontSize: 11, color: 'var(--text-3)', fontStyle: 'italic', paddingLeft: 2 }}>
                {describeNode(node)}
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={{ marginTop: 8 }}>
        <button type="button" className="btn ghost sm" style={{ fontSize: 11 }}
                onClick={() => onChange([...list, emptyOutstandingRequirement()])}>
          + Outstanding requirement
        </button>
      </div>
    </div>
  );
}

function conditionStepError(draft) {
  const tree = draft.conditions;
  if (!isGroup(tree) || !(tree.rules || []).length) return null;   // empty = fires on everything
  const allowed = new Set(fieldsForRuleKind(draft.kind, draft.triggers).map(f => f.key));
  const bad = findDisallowedNode(tree, allowed);
  if (bad?.aggregate) return 'Cross-case count conditions are not available in notification rules — remove them.';
  if (bad?.nestedOutstanding) return NESTED_OUTSTANDING_MESSAGE;
  if (bad?.field) {
    return `The “${FIELD_LABELS[bad.field] || bad.field}” condition is not available for this ${
      draft.kind === 'scheduled' ? 'scheduled rule' : 'trigger selection'} — change or remove it.`;
  }
  if (!ruleIsPreviewable(tree)) return 'Finish or remove the incomplete condition rows.';
  return null;
}

function specListError(list, where = '') {
  if (!list.length) return `${where}Add at least one recipient.`;
  for (let i = 0; i < list.length; i++) {
    const s = list[i] || {};
    if (s.kind === 'email' && !isStaffEmail(s.value)) {
      return `${where}Recipient ${i + 1}: enter a staff email address (…@therapieclinic.com).`;
    }
    if ((s.kind === 'jobTitle' || s.kind === 'appRole') && !String(s.value || '').trim()) {
      return `${where}Recipient ${i + 1}: pick a role or job title.`;
    }
    if (s.kind === 'group' && !String(s.value || '').trim()) {
      return `${where}Recipient ${i + 1}: pick a recipient group.`;
    }
  }
  if (list.every(s => s?.channel === EXCEPT_CHANNEL)) return `${where}Add at least one To, Cc or Bcc recipient — Except only removes people.`;
  return null;
}

function recipientStepError(draft) {
  const base = specListError(draft.recipients);
  if (base) return base;
  if (draft.kind !== 'scheduled' || !draft.escalation.length) return null;
  let prev = findDayThreshold(draft.conditions);
  if (prev == null) return 'Escalation needs a day-count condition in step 2 (e.g. “outstanding for at least N days”) — add one or remove the escalation steps.';
  for (let i = 0; i < draft.escalation.length; i++) {
    const st = draft.escalation[i];
    const where = `Escalation step ${i + 2}: `;
    if (!Number.isInteger(st.afterDays) || st.afterDays <= prev) return `${where}days must be more than ${prev}.`;
    prev = st.afterDays;
    const e = specListError(st.recipients || [], where);
    if (e) return e;
  }
  return null;
}

function errorForStep(step, draft) {
  if (step === 1) {
    if (draft.kind === 'event' && draft.triggers.length === 0) return 'Pick at least one trigger.';
    if (draft.kind === 'event' && draft.coalesceOn
        && draft.coalesceMaxWindowMinutes < draft.coalesceWindowMinutes) {
      return 'The maximum cap must be at least the quiet-period.';
    }
    return null;
  }
  if (step === 2) return conditionStepError(draft);
  if (step === 3) return recipientStepError(draft);
  return null;
}

// ── Review summary — one sentence describing the whole rule ──────────────────
function summarySentence(draft, groups) {
  const rec = recipientSummary(draft.recipients, groups);
  const hasConds = isGroup(draft.conditions) && (draft.conditions.rules || []).length > 0;
  if (draft.kind === 'scheduled') {
    const refire = draft.refire === 'rearm_on_activity'
      ? 're-arming after new case activity'
      : draft.refire === 'every_run'
        ? 'as a daily report, every scan'
        : 'once per case';
    const shape = draft.perCase ? 'one email per case' : 'a digest';
    const base = findDayThreshold(draft.conditions);
    if (draft.escalation.length && base != null) {
      const steps = [`from ${base} days → ${rec}`,
        ...draft.escalation.map(st => `from ${st.afterDays} days → ${recipientSummary(st.recipients, groups)}`)];
      return `Every day at 08:00 UTC, send ${shape} for open cases matching the conditions (${refire}): ${steps.join('; ')}. Each case only gets its highest step.`;
    }
    return `Every day at 08:00 UTC, send ${shape} to ${rec} for open cases${hasConds ? ' matching the conditions' : ''} (${refire}).`;
  }
  const when = draft.triggers.map(t => TRIGGER_LABELS[t] || t).join(' or ') || '(no trigger)';
  const batched = draft.coalesceOn
    ? ` Rapid updates are batched into one digest — sent after ${draft.coalesceWindowMinutes} min of quiet, or ${draft.coalesceMaxWindowMinutes} min at most.`
    : '';
  return `On ${when}, notify ${rec}${hasConds ? ' when the conditions match' : ' for every case'}.${batched}`;
}

// ── Small chrome pieces (NewCaseWizard idiom, compacted for 5 steps) ──────────

// ── Escalation steps (scheduled rules) — later recipients at higher day counts ──
function EscalationEditor({ steps, baseDays, onChange, groups }) {
  const setAt = (i, next) => onChange(steps.map((st, j) => (j === i ? next : st)));
  const lastDays = steps.length ? steps[steps.length - 1].afterDays : baseDays;
  const add = () => onChange([...steps, { afterDays: (Number.isInteger(lastDays) ? lastDays : 0) + 4, recipients: [] }]);
  return (
    <div style={{ marginTop: 18, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
      <div className="admin-section-title" style={{ marginBottom: 4 }}>Escalation</div>
      <div className="field-hint" style={{ marginTop: 0, marginBottom: 10 }}>
        {baseDays == null
          ? 'Add a day-count condition in step 2 (e.g. “outstanding for at least N days”) to escalate to other people as the days go on.'
          : `The recipients above get it from ${baseDays} days. Add a step to send to someone else later on — each case only gets its highest step, and stops once the conditions no longer match.`}
      </div>
      {steps.map((st, i) => (
        <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 12.5 }}>
            <strong style={{ fontWeight: 600 }}>Step {i + 2} · from</strong>
            <input type="number" className="input" min={1} max={3650} value={Number.isInteger(st.afterDays) ? st.afterDays : ''}
              onChange={e => setAt(i, { ...st, afterDays: e.target.value === '' ? null : Math.trunc(Number(e.target.value)) })}
              style={{ width: 72, fontSize: 13, padding: '4px 8px' }} aria-label={`Step ${i + 2} days`} />
            <span>days</span>
            <span style={{ flex: 1 }} />
            <button type="button" className="btn ghost sm" onClick={() => onChange(steps.filter((_, j) => j !== i))}
              style={{ fontSize: 12, color: 'var(--danger)' }}>Remove step</button>
          </div>
          <RecipientEditor specs={st.recipients || []} onChange={next => setAt(i, { ...st, recipients: next })} groups={groups} />
        </div>
      ))}
      <button type="button" className="btn ghost sm" onClick={add}
        disabled={baseDays == null || steps.length >= MAX_ESCALATION_STEPS} style={{ fontSize: 12 }}>
        + Add escalation step
      </button>
    </div>
  );
}

function StepPill({ n, label, active, done, clickable, onClick }) {
  return (
    <button type="button" onClick={onClick} disabled={!clickable} style={{
      display: 'flex', alignItems: 'center', gap: 8,
      flex: 1, minWidth: 0, padding: '8px 10px',
      background: active ? 'var(--surface)' : 'var(--surface-2)',
      border: '1px solid', borderColor: active ? 'var(--brand)' : 'var(--border)',
      borderRadius: 10, cursor: clickable ? 'pointer' : 'default',
      transition: 'all 200ms var(--ease)',
    }}>
      <span style={{
        display: 'grid', placeItems: 'center',
        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
        background: active ? 'var(--brand)' : (done ? 'var(--ok)' : 'var(--surface-3)'),
        color: (active || done) ? '#fff' : 'var(--text-3)',
        fontWeight: 600, fontSize: 11,
      }}>
        {done && !active ? '✓' : n}
      </span>
      <span style={{
        fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap',
        overflow: 'hidden', textOverflow: 'ellipsis',
        color: active ? 'var(--text)' : 'var(--text-2)',
      }}>
        {label}
      </span>
    </button>
  );
}

// The two-way kind picker card (radio semantics, button chrome).
function KindCard({ active, title, sub, onClick }) {
  return (
    <button type="button" onClick={onClick} role="radio" aria-checked={active} style={{
      flex: '1 1 240px', textAlign: 'left', padding: '10px 12px',
      background: active ? 'var(--brand-weak)' : 'var(--surface)',
      border: '1px solid', borderColor: active ? 'var(--brand)' : 'var(--border)',
      borderRadius: 10, cursor: 'pointer', transition: 'all 200ms var(--ease)',
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{title}</div>
      <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>{sub}</div>
    </button>
  );
}

const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

// ── Page ──────────────────────────────────────────────────────────────────────

export default function NotificationRuleWizard() {
  const navigate = useNavigate();
  const { id: routeId } = useParams();          // undefined on the /new route
  const isNew = !routeId;
  const { config, loading, updateConfig } = useAppConfig();

  const savedRules = Array.isArray(config?.notificationRules) ? config.notificationRules : [];
  const groups = Array.isArray(config?.recipientGroups) ? config.recipientGroups : [];
  const schedOn = config?.scheduledNotificationsEnabled === true;
  const stored = routeId ? savedRules.find(r => r?.id === routeId) : null;

  // Draft seeds once: immediately for a new rule, after config load for an
  // edit (then stays local — a background config refresh must not clobber it).
  const [draft, setDraft] = useState(() => (isNew ? freshDraft() : null));
  useEffect(() => {
    if (isNew || draft || loading || !stored) return;
    setDraft(draftFromRule(stored));
  }, [isNew, draft, loading, stored]);

  const [step, setStep] = useState(1);
  // Editing an existing (complete) rule unlocks every step pill immediately.
  const [maxStep, setMaxStep] = useState(() => (isNew ? 1 : STEPS.length));
  const [showStepError, setShowStepError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // TherapieMaps feeds the clinic/region/country value dropdowns (same as the
  // old Notifications tab editor). Soft-fail: pickers fall back to free text.
  const [tmData, setTmData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getTherapieMapsData()
      .then(data => { if (!cancelled) setTmData(data); })
      .catch(() => { if (!cancelled) setTmData(null); });
    return () => { cancelled = true; };
  }, []);

  const fieldValues = useMemo(() => buildRuleFieldValues(config, tmData), [config, tmData]);

  // ── Live preview (step 5) — 400 ms debounce + stale-response guard, the
  // same pattern as the TagRuleEditor / old Notifications tab preview. ──
  const finalRule = useMemo(() => (draft ? ruleFromDraft(draft) : null), [draft]);
  const ruleErrors = useMemo(
    () => (finalRule ? validateNotificationRule(finalRule) : []),
    [finalRule],
  );
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const canPreview = step === 5 && !!finalRule && ruleErrors.length === 0;
  const finalRuleKey = JSON.stringify(finalRule);

  useEffect(() => {
    if (!canPreview) {
      setPreview(null);
      setPreviewBusy(false);
      setPreviewError(null);
      return;
    }
    let cancelled = false;   // stale-response guard
    const timer = setTimeout(async () => {   // debounce
      setPreviewBusy(true);
      setPreviewError(null);
      try {
        const r = await previewNotificationRule(finalRule);
        if (!cancelled) setPreview(r);
      } catch (e) {
        if (!cancelled) { setPreview(null); setPreviewError(e.message || 'Preview failed.'); }
      } finally {
        if (!cancelled) setPreviewBusy(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [canPreview, finalRuleKey]);

  useEffect(() => { setShowStepError(false); }, [step]);

  const backToTab = () => navigate('/admin', { state: { adminTab: 'Notifications' } });

  const set = (patch) => setDraft(d => ({ ...d, ...patch }));

  function toggleTrigger(t) {
    setDraft(d => ({
      ...d,
      triggers: d.triggers.includes(t) ? d.triggers.filter(x => x !== t) : [...d.triggers, t],
    }));
  }

  const currentError = draft ? errorForStep(step, draft) : null;

  function goNext() {
    if (currentError) { setShowStepError(true); return; }
    setShowStepError(false);
    const next = Math.min(STEPS.length, step + 1);
    setStep(next);
    setMaxStep(m => Math.max(m, next));
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  function goBack() {
    setStep(s => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  async function handleSave() {
    const rule = ruleFromDraft(draft);
    const errs = validateNotificationRule(rule);
    if (errs.length) { setSaveError(errs[0]); return; }
    setSaving(true);
    setSaveError(null);
    try {
      // Upsert into the CURRENT published array (list-management edits live on
      // the tab; this page only ever touches its own rule).
      const existing = Array.isArray(config?.notificationRules) ? config.notificationRules : [];
      const idx = existing.findIndex(r => r?.id === rule.id);
      const next = idx >= 0 ? existing.map((r, i) => (i === idx ? rule : r)) : [...existing, rule];
      await updateConfig('notificationRules', next, { publish: true });
      backToTab();
    } catch (e) {
      setSaveError(e?.message || 'Failed to save the rule.');
    } finally {
      setSaving(false);
    }
  }

  // ── Not-found / loading states ──────────────────────────────────────────
  if (!draft) {
    const notFound = !isNew && !loading && !stored;
    return (
      <>
        <header className="page-head" style={{ maxWidth: 860 }}>
          <div>
            <div className="breadcrumb">
              <a href="#" onClick={e => { e.preventDefault(); backToTab(); }}>Admin Panel</a>
              <span>›</span>
              <span>Notification rule</span>
            </div>
            <h1>{notFound ? 'Rule not found' : 'Notification rule'}</h1>
          </div>
        </header>
        {notFound ? (
          <>
            <div className="callout warn" style={{ marginBottom: 16, maxWidth: 860 }}>
              <AlertIcon />
              <div>No notification rule with id <code>{routeId}</code> exists — it may have been deleted.</div>
            </div>
            <button className="btn" type="button" onClick={backToTab}>Back to Notifications</button>
          </>
        ) : (
          <div className="field-hint">Loading rule…</div>
        )}
      </>
    );
  }

  const conditionCount = isGroup(draft.conditions) ? (draft.conditions.rules || []).length : 0;
  const previewCases = Array.isArray(preview?.cases) ? preview.cases : [];
  const shownCases = previewCases.slice(0, 8);
  const moreCases = Math.max(0, (preview?.matches ?? 0) - shownCases.length);

  return (
    <>
      <header className="page-head" style={{ maxWidth: 860 }}>
        <div>
          <div className="breadcrumb">
            <a href="#" onClick={e => { e.preventDefault(); backToTab(); }}>Admin Panel</a>
            <span>›</span>
            <span>Notifications</span>
            <span>›</span>
            <span>{isNew ? 'New rule' : 'Edit rule'}</span>
          </div>
          <h1>{isNew ? 'New notification rule' : 'Edit notification rule'}</h1>
          <div className="page-sub">
            Choose when it runs, which cases it matches and who it notifies — then review a live preview before saving.
          </div>
        </div>
        <div className="head-actions">
          <button className="btn" type="button" onClick={backToTab}>Cancel</button>
        </div>
      </header>

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, maxWidth: 860, flexWrap: 'wrap' }}>
        {STEPS.map((label, i) => {
          const n = i + 1;
          return (
            <StepPill key={label} n={n} label={label}
                      active={step === n} done={n < step}
                      clickable={n <= maxStep && n !== step}
                      onClick={() => { setStep(n); window.scrollTo({ top: 0, behavior: 'instant' }); }} />
          );
        })}
      </div>

      <div className="panel" style={{ maxWidth: 860 }}>
        <div className="panel-body">
          {/* Name + enabled — persistent across steps */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
                        paddingBottom: 14, marginBottom: 16, borderBottom: '1px solid var(--border)' }}>
            <input type="text" className="input" placeholder="Rule name (e.g. Code 4/5 → leadership)"
                   value={draft.name} onChange={e => set({ name: e.target.value })}
                   maxLength={120} aria-label="Rule name"
                   style={{ flex: 1, minWidth: 240, fontSize: 13, padding: '8px 10px' }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={draft.enabled}
                     onChange={e => set({ enabled: e.target.checked })}
                     style={{ accentColor: 'var(--brand)', width: 15, height: 15 }} />
              Enabled
            </label>
          </div>

          {/* ── Step 1 · When ── */}
          {step === 1 && (
            <div>
              <div className="admin-section-title" style={{ marginBottom: 8 }}>When should this rule run?</div>
              <div role="radiogroup" aria-label="Rule kind" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <KindCard active={draft.kind === 'event'}
                          title="When an event happens"
                          sub="Fires immediately when a matching case event occurs."
                          onClick={() => set({ kind: 'event' })} />
                <KindCard active={draft.kind === 'scheduled'}
                          title="On a daily schedule"
                          sub="Checked once a day at 08:00 UTC against open cases."
                          onClick={() => set({ kind: 'scheduled' })} />
              </div>

              {draft.kind === 'event' ? (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Triggers</div>
                  <div className="field-hint" style={{ marginTop: 0, marginBottom: 8 }}>
                    Pick one or more case events. The rule fires once per case write, however many of its triggers that write sets off.
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6 }}>
                    {NOTIFICATION_TRIGGERS.map(t => {
                      const on = draft.triggers.includes(t);
                      return (
                        <label key={t} style={{
                          display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
                          padding: '7px 10px', borderRadius: 8, cursor: 'pointer',
                          border: '1px solid', borderColor: on ? 'var(--brand)' : 'var(--border)',
                          background: on ? 'var(--brand-weak)' : 'var(--surface)',
                        }}>
                          <input type="checkbox" checked={on} onChange={() => toggleTrigger(t)}
                                 style={{ accentColor: 'var(--brand)', width: 15, height: 15 }} />
                          {TRIGGER_LABELS[t] || t}
                        </label>
                      );
                    })}
                  </div>
                  <div className="field-hint">
                    Selecting Status changed or Severity changed also unlocks “previous value” conditions in the next step.
                  </div>
                  <div className="field-hint">
                    Selecting Requirement added or Sign-off completed also unlocks requirement-type conditions in the next step.
                  </div>

                  {/* Batch rapid updates into one digest (coalesce) — event rules only. */}
                  <div style={{ marginTop: 16, padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!draft.coalesceOn}
                             onChange={e => set({ coalesceOn: e.target.checked })}
                             style={{ accentColor: 'var(--brand)', width: 15, height: 15 }} />
                      Batch rapid updates into one digest
                    </label>
                    {draft.coalesceOn && (
                      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 13, color: 'var(--text-2)', width: 92 }}>Quiet period</span>
                          <input type="number" className="input"
                                 min={COALESCE_WINDOW_MIN} max={COALESCE_WINDOW_MAX}
                                 value={draft.coalesceWindowMinutes}
                                 onChange={e => {
                                   const raw = parseInt(e.target.value, 10);
                                   const clamped = Number.isFinite(raw)
                                     ? Math.min(COALESCE_WINDOW_MAX, Math.max(COALESCE_WINDOW_MIN, raw))
                                     : COALESCE_WINDOW_MIN;
                                   // Keep the cap at or above the quiet-period.
                                   set({
                                     coalesceWindowMinutes: clamped,
                                     coalesceMaxWindowMinutes: Math.max(clamped, draft.coalesceMaxWindowMinutes),
                                   });
                                 }}
                                 style={{ fontSize: 13, padding: '6px 8px', width: 72 }}
                                 aria-label="Coalesce quiet-period in minutes" />
                          <span style={{ fontSize: 13, color: 'var(--text-2)' }}>minutes of no new updates</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 13, color: 'var(--text-2)', width: 92 }}>Max cap</span>
                          <input type="number" className="input"
                                 min={draft.coalesceWindowMinutes} max={COALESCE_MAX_WINDOW_MAX}
                                 value={draft.coalesceMaxWindowMinutes}
                                 onChange={e => {
                                   const raw = parseInt(e.target.value, 10);
                                   const clamped = Number.isFinite(raw)
                                     ? Math.min(COALESCE_MAX_WINDOW_MAX, Math.max(draft.coalesceWindowMinutes, raw))
                                     : draft.coalesceWindowMinutes;
                                   set({ coalesceMaxWindowMinutes: clamped });
                                 }}
                                 style={{ fontSize: 13, padding: '6px 8px', width: 72 }}
                                 aria-label="Coalesce maximum cap in minutes" />
                          <span style={{ fontSize: 13, color: 'var(--text-2)' }}>minutes after the first update</span>
                        </div>
                      </div>
                    )}
                    <div className="field-hint" style={{ marginBottom: 0 }}>
                      Waits until the case has been quiet for{' '}
                      {draft.coalesceOn ? draft.coalesceWindowMinutes : COALESCE_WINDOW_DEFAULT} minutes, then sends one
                      digest — or at most {draft.coalesceOn ? draft.coalesceMaxWindowMinutes : COALESCE_MAX_WINDOW_DEFAULT} minutes
                      after the first update, whichever comes first. (Checked every couple of minutes.)
                      Leave off for urgent rules (e.g. severity escalations).
                    </div>
                  </div>

                  {draft.triggers.includes('comment_added_private') && (
                    <div className="callout warn" style={{ marginTop: 12, fontSize: 13 }}>
                      <AlertIcon />
                      <div>
                        <strong>Private comments are restricted content.</strong> Every recipient of this
                        rule must hold a role that can see private comments — the email reveals that a
                        private note exists (never its content), and un-cleared recipients cannot open it.
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div className="field-hint" style={{ margin: 0 }}>
                    A daily scan at 08:00 UTC evaluates scheduled rules against every open case.
                    It only sends while <strong>Scheduled sending</strong> is switched on in
                    Admin ▸ Notifications (currently {schedOn ? 'ON' : 'OFF'}).
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Re-fire mode</div>
                    <div role="radiogroup" aria-label="Re-fire mode" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                        <input type="radio" name="refire" checked={draft.refire === 'once'}
                               onChange={() => set({ refire: 'once' })}
                               style={{ accentColor: 'var(--brand)', marginTop: 2 }} />
                        <span>
                          <strong style={{ fontWeight: 600 }}>Once per case</strong>
                          <span style={{ color: 'var(--text-3)' }}> — after this rule fires for a case, it never fires for that case again.</span>
                        </span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                        <input type="radio" name="refire" checked={draft.refire === 'rearm_on_activity'}
                               onChange={() => set({ refire: 'rearm_on_activity' })}
                               style={{ accentColor: 'var(--brand)', marginTop: 2 }} />
                        <span>
                          <strong style={{ fontWeight: 600 }}>Re-arm on activity</strong>
                          <span style={{ color: 'var(--text-3)' }}> — fires again once there has been new case activity since the last send.</span>
                        </span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                        <input type="radio" name="refire" checked={draft.refire === 'every_run'}
                               onChange={() => set({ refire: 'every_run' })}
                               style={{ accentColor: 'var(--brand)', marginTop: 2 }} />
                        <span>
                          <strong style={{ fontWeight: 600 }}>Daily report — sends every scan (08:00 UTC), no dedup</strong>
                          <span style={{ color: 'var(--text-3)' }}> — each region-/clinic-based recipient only sees their own cases.</span>
                        </span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                      <input type="checkbox" checked={draft.perCase === true}
                             onChange={e => set({ perCase: e.target.checked })}
                             style={{ accentColor: 'var(--brand)', marginTop: 2 }} />
                      <span>
                        <strong style={{ fontWeight: 600 }}>One email per case</strong>
                        <span style={{ color: 'var(--text-3)' }}> — each matching case gets its own reminder (Case ID, clinic, severity, outstanding items and link) instead of one digest table per recipient.</span>
                      </span>
                    </label>
                    <div className="field-hint" style={{ marginTop: 4, marginBottom: 0, paddingLeft: 24 }}>
                      Recipients are resolved per case, so CC recipients (e.g. Head of RO, Country manager) land on the same email as the clinic&rsquo;s To.
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Step 2 · Conditions ── */}
          {step === 2 && (
            <div>
              <div className="admin-section-title" style={{ marginBottom: 8 }}>Which cases should it match?</div>
              <div className="field-hint" style={{ marginTop: 0, marginBottom: 10 }}>
                {draft.kind === 'scheduled'
                  ? 'Leave empty to include every open case in the daily scan. Day-count fields (e.g. days since last activity) and outstanding-requirement checks are available here.'
                  : 'Leave empty to fire on every matching event. OPS narrows to IOI or GB; file counts check this case’s uploads; outstanding requirements check what is still missing.'}
              </div>
              {(() => {
                const { base, outstanding } = splitOutstanding(draft.conditions);
                return (
                  <>
                    <RuleBuilder
                      value={base}
                      onChange={next => set({ conditions: joinOutstanding(next, outstanding) })}
                      fieldValues={fieldValues}
                      showEnabled={false}
                      fields={fieldsForRuleKind(draft.kind, draft.triggers)}
                      allowAggregate={false}
                      allowFileCount={true}
                    />
                    <OutstandingRequirementEditor
                      nodes={outstanding}
                      onChange={nodes => set({ conditions: joinOutstanding(base, nodes) })}
                    />
                  </>
                );
              })()}
              {fileCountAtSubmitWarning(draft) && (
                <div className="callout warn" style={{ marginTop: 12, fontSize: 13 }}>
                  <AlertIcon />
                  <div>
                    File counts are <strong>zero at submission time</strong> — the wizard uploads its
                    files after the case is created, so a rule fired only by{' '}
                    <strong>{TRIGGER_LABELS.case_submitted}</strong> never sees them. Add the{' '}
                    <strong>{TRIGGER_LABELS.attachment_uploaded}</strong> trigger (step 1) for reliable
                    file-presence rules.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Step 3 · Recipients ── */}
          {step === 3 && (
            <div>
              <div className="admin-section-title" style={{ marginBottom: 8 }}>Who should be notified?</div>
              <div className="field-hint" style={{ marginTop: 0, marginBottom: 10 }}>
                Staff addresses only (therapieclinic.com), max 50 per send. Recipient groups are managed under{' '}
                <a href="#" onClick={e => { e.preventDefault(); backToTab(); }}>Admin ▸ Notifications ▸ Recipient groups</a>.
              </div>
              {draft.kind === 'scheduled' && draft.escalation.length > 0 && (
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>
                  Step 1 · from {findDayThreshold(draft.conditions) ?? '?'} days
                </div>
              )}
              <RecipientEditor
                specs={draft.recipients}
                onChange={next => set({ recipients: next })}
                groups={groups}
              />
              {draft.kind === 'scheduled' && (
                <EscalationEditor
                  steps={draft.escalation}
                  baseDays={findDayThreshold(draft.conditions)}
                  onChange={next => set({ escalation: next })}
                  groups={groups}
                />
              )}
            </div>
          )}

          {/* ── Step 4 · Message ── */}
          {step === 4 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="admin-section-title" style={{ marginBottom: -6 }}>What should the email say?</div>
              <div>
                <label htmlFor="rule-subject-suffix" style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  Subject suffix <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>(optional)</span>
                </label>
                <input id="rule-subject-suffix" type="text" className="input"
                       value={draft.subjectSuffix} maxLength={SUBJECT_SUFFIX_MAX}
                       onChange={e => set({ subjectSuffix: e.target.value })}
                       placeholder="e.g. Escalation — leadership"
                       style={{ fontSize: 13, padding: '8px 10px', width: '100%' }} />
                <div className="field-hint">
                  Appended to the subject, e.g. “AE-1042 · Case severity changed{draft.subjectSuffix.trim() ? ` · ${draft.subjectSuffix.trim()}` : ' · …'}”.
                  {' '}{draft.subjectSuffix.length}/{SUBJECT_SUFFIX_MAX}
                </div>
              </div>
              <div>
                <label htmlFor="rule-note" style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  Note <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>(optional)</span>
                </label>
                <textarea id="rule-note" className="input" rows={3}
                          value={draft.note} maxLength={NOTE_MAX}
                          onChange={e => set({ note: e.target.value })}
                          placeholder="One short line added to the email body."
                          style={{ fontSize: 13, padding: '8px 10px', width: '100%', resize: 'vertical' }} />
                <div className="field-hint">
                  Never include client details — recipients get the Case ID, clinic and a link only.
                  {' '}{draft.note.length}/{NOTE_MAX}
                </div>
              </div>
            </div>
          )}

          {/* ── Step 5 · Review & preview ── */}
          {step === 5 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="admin-section-title" style={{ marginBottom: -4 }}>Review</div>
              <div style={{ fontSize: 13, color: 'var(--text)' }}>
                {summarySentence(draft, groups)}
                {conditionCount > 0 && (
                  <span style={{ color: 'var(--text-3)' }}>
                    {' '}({conditionCount} top-level condition{conditionCount === 1 ? '' : 's'})
                  </span>
                )}
                {!draft.enabled && (
                  <span style={{ color: 'var(--text-3)' }}> Saved as disabled — it will not run until enabled.</span>
                )}
              </div>

              {ruleErrors.length > 0 && (
                <div className="callout danger" style={{ fontSize: 13 }}>
                  <AlertIcon />
                  <div>
                    <strong>Fix before saving:</strong>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                      {ruleErrors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </div>
                </div>
              )}

              {/* Live dry-run against current cases — nothing is sent or saved. */}
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }} aria-live="polite">
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Live preview</div>
                {!canPreview && ruleErrors.length > 0 && (
                  <div className="field-hint" style={{ margin: 0 }}>Complete the rule to see the preview.</div>
                )}
                {previewBusy && <div className="field-hint" style={{ margin: 0 }}>Checking current cases…</div>}
                {previewError && <div style={{ fontSize: 12, color: 'var(--danger)' }}>{previewError}</div>}
                {!previewBusy && !previewError && preview && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ fontSize: 13 }}>
                      Matches <strong>{preview.matches}{preview.capped ? '+' : ''}</strong> of {preview.total} live case{preview.total === 1 ? '' : 's'}
                      {preview.capped && <span style={{ color: 'var(--text-3)' }}> (preview scans the first {preview.scanned})</span>}.
                      {draft.kind === 'event' && (
                        <span style={{ color: 'var(--text-3)' }}>
                          {draft.triggers.some(t => t.startsWith('requirement'))
                            ? ' Counts cases whose current requirements (matrix-driven or added) match — the rule itself fires when a requirement appears on a case, including at case creation.'
                            : ' Existing cases are a guide — the rule only fires on new events.'}
                        </span>
                      )}
                    </div>

                    {preview.scheduled && (
                      <div style={{ fontSize: 13 }}>
                        {draft.refire === 'every_run' ? (
                          <>Would include <strong>{preview.scheduled.eligible}</strong> case{preview.scheduled.eligible === 1 ? '' : 's'} every day.</>
                        ) : (
                          <>Would fire today for <strong>{preview.scheduled.eligible}</strong> case{preview.scheduled.eligible === 1 ? '' : 's'}
                          {' '}({preview.scheduled.suppressedByDedup} suppressed by dedup).</>
                        )}
                        {preview.scheduled.perCase && (
                          <span style={{ color: 'var(--text-3)' }}> One email per case — {preview.scheduled.eligible} email{preview.scheduled.eligible === 1 ? '' : 's'}, recipients resolved per case.</span>
                        )}
                      </div>
                    )}

                    {Array.isArray(preview.escalation) && (
                      <div style={{ fontSize: 13 }}>
                        By step today:{' '}
                        {preview.escalation.map(st => (
                          <span key={st.step} style={{ marginRight: 10 }}>
                            step {st.step} ({st.days}+ days): <strong>{st.matches}</strong>
                          </span>
                        ))}
                      </div>
                    )}

                    {shownCases.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-3)', marginBottom: 4 }}>Matching cases</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {shownCases.map(c => (
                            <div key={c.id} style={{ fontSize: 12, color: 'var(--text-2)' }}>
                              AE-{c.id} · {c.clinic || '—'} · {c.treatment || '—'} · {c.severity || '—'} · {c.status || '—'}
                              {typeof c.matchedValue === 'number' && (
                                <span style={{ color: 'var(--text-3)' }}> · {c.matchedValue} days</span>
                              )}
                              {typeof c.matchedValue === 'string' && c.matchedValue && (
                                <span style={{ color: 'var(--text-3)' }}> · {c.matchedValue}</span>
                              )}
                            </div>
                          ))}
                        </div>
                        {moreCases > 0 && (
                          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>…and {moreCases} more</div>
                        )}
                      </div>
                    )}

                    {Array.isArray(preview.recipients) && preview.recipients.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-3)', marginBottom: 4 }}>Resolved recipients</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {preview.recipients.map((r, i) => (
                            <div key={i} style={{ fontSize: 12, color: 'var(--text-2)' }}>
                              <span style={{ textTransform: 'uppercase', fontSize: 10.5, fontWeight: 700, color: 'var(--text-3)' }}>{r.channel || 'to'}</span>
                              {' '}{KIND_LABELS[r.kind] || r.kind}{r.value ? ` (${r.value})` : ''} →{' '}
                              {r.addresses?.length ? r.addresses.join(', ') : 'no addresses resolved'}
                              {r.note && <span style={{ color: 'var(--text-3)' }}> — {r.note}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {saveError && (
                <div className="callout danger" style={{ fontSize: 13 }}>
                  <AlertIcon />
                  <div><strong>Save failed</strong> — {saveError}</div>
                </div>
              )}
            </div>
          )}

          {/* Step-local validation nudge (shown after a blocked Next). */}
          {showStepError && currentError && (
            <div className="callout danger" style={{ marginTop: 14, fontSize: 13 }}>
              <AlertIcon />
              <div>{currentError}</div>
            </div>
          )}

          {/* ── Step nav ── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        paddingTop: 16, marginTop: 16, borderTop: '1px solid var(--border)' }}>
            {step === 1 ? (
              <button className="btn" type="button" onClick={backToTab}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                Cancel
              </button>
            ) : (
              <button className="btn" type="button" onClick={goBack}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                Back
              </button>
            )}
            {step < STEPS.length ? (
              <button className="btn primary" type="button" onClick={goNext}>
                Next
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            ) : (
              <button className="btn primary" type="button" onClick={handleSave}
                      disabled={saving || ruleErrors.length > 0}>
                {saving ? 'Saving…' : (isNew ? 'Create rule' : 'Save rule')}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
